import { useLazyQuery } from '@apollo/client';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { PROJECTS_BY_EMP_ID } from '../../api/query';
import Loader from '../../components/common/Loader';
import ioniconaaddoutline from "../../assets/dashboard/ioniconaaddoutline.svg";
import tripleIcons from "../../assets/task/triple_dot.svg";
import defaultProjectImg from "../../assets/project/default-project.svg";
import { useAuth } from '../../context/AuthContext';
import BreadCumb from '../../components/common/Breadcrumb';
import { useNavigate } from 'react-router-dom';
import { showError } from '../../components/common/ToastNotification';

const Projects: FunctionComponent = () => {
    const navigate = useNavigate();
    const { userInfo, logout } = useAuth();
    const employeeID = userInfo?.employee_id || '';
    const organizationID = userInfo?.agencies?.id || '';

    const [activeMenu, setActiveMenu] = useState<string | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const [
        getProjectsByEmpID,
        { data: projectsData, loading: projectsDataLoading, error },
    ] = useLazyQuery(PROJECTS_BY_EMP_ID, {
        fetchPolicy: 'no-cache',
    });

    if (error) {
        if (
            error.message.includes("Session has expired. Please login again.") ||
            error.message.includes("Unauthorized access, please login again.")
        ) {
            showError(error.message);
            logout();
            navigate('/');
        }
    }

    useEffect(() => {
        if (employeeID) {
            getProjectsByEmpID({
                variables: {
                    emp_id: employeeID,
                    organization_id: organizationID,
                },
            });
        }
    }, [employeeID]);

    // Close menu on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setActiveMenu(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <>
            <div id="cts-respon-p-dv" className="w-full relative min-h-screen overflow-hidden shrink-0 flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4 box-border text-left text-base text-gray font-manrope">
                <div className="flex-1 flex flex-col items-start justify-start gap-6">
                    <BreadCumb />
                    <div className="self-stretch flex flex-row items-center justify-between gap-[5px] text-2xl">
                        <h3 className="m-0  relative text-inherit font-bold font-inherit  mq450:text-base">My Projects</h3>
                        <div
                            className="flex flex-row flex-col items-center justify-start text-center text-base text-white"
                            onClick={() => navigate('/add_project')}
                        >
                            {/* <div className="rounded-sm bg-dimgray flex flex-row items-center justify-center py-1 px-2 gap-1 cursor-pointer">
                                <div className="relative">Add Project</div>
                                <img className="w-4 h-4" alt="" src="post-icon.svg" />
                            </div> */}
                            <div className=" max-w-[120px] cursor-pointer flex gap-[8px] items-start justify-start p-[10px] plus-task-btn taskdash-plush-img rounded">
                                <span> Add Project</span>
                                <img
                                    className="w-4 h-4 relative overflow-hidden shrink-0"
                                    loading="lazy"
                                    alt=""
                                    src={ioniconaaddoutline}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 gap-spacing-lg box-border max-w-full mq575:p-3">
                        <div className="grid grid-cols-4 mq1400:grid-4 mq1356:grid-3 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 gap-[16px] min-w-full">
                            {projectsData?.projects_by_emp_id?.map((project: any) => (
                                <div
                                    key={project.id}
                                    className="relative rounded-lg bg-white border-whitesmoke project-card-iterms-dv border-solid border-[1px] box-border flex flex-col items-center justify-center p-4 gap-4 text-[5px] text-darkslategray font-poppins"
                                >
                                    <div className="absolute top-2 right-2 z-10">
                                        <div
                                            className="cursor-pointer text-gray-700 relative text-5xl"
                                            onClick={() => setActiveMenu(prev => (prev === project.id ? null : project.id))}
                                        >
                                            <img
                                                className="w-4 h-4 relative overflow-hidden shrink-0 absolute top-[15px] left-[-5px]"
                                                loading="lazy"
                                                alt=""
                                                src={tripleIcons}
                                            />
                                        </div>

                                        {activeMenu === project.id && (
                                            <div
                                                ref={menuRef}
                                                className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 shadow-lg rounded-md z-20"
                                            >
                                                <div
                                                    className="px-4 py-2  cursor-pointer"
                                                    onClick={() => {
                                                        setActiveMenu(null);
                                                        // Navigate to edit project page
                                                        navigate('/edit_project', {
                                                            state: {
                                                                projectID: project.id,
                                                                projectName: project?.name,
                                                                projectStatusID: project?.status_id,
                                                                projectTagLine: project?.tagline,
                                                                projectNiche: project?.niche,
                                                                projectIdealCustomerProfile: project?.ideal_customer_profile,
                                                                projectUniqueAboutIt: project?.unique_about_it,
                                                                projectMembers: project?.members,
                                                                projectCreatedBy: project?.created_by,
                                                                projectImgUrl: project?.image_url
                                                            },
                                                        });
                                                    }}
                                                >
                                                    Edit Project
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div
                                        className="grid w-full justify-center cursor-pointer text-center"
                                        onClick={() => {
                                            // Clear dates from localStorage
                                            localStorage.removeItem("kanbanGoalStartDate");
                                            localStorage.removeItem("kanbanGoalEndDate");
                                            navigate('/goals', {
                                                state: {
                                                    projectID: project.id,
                                                    projectName: project?.name,
                                                    projectImage: project?.image_url,
                                                    projectMembers: project?.members || [],
                                                },
                                            })
                                        }
                                        }
                                    >
                                        <div className="w-[200px] relative h-[80px] project-logo-item-dv">
                                            <img
                                                className="max-w-full overflow-hidden h-[80px] object-contain"
                                                alt=""
                                                src={project?.image_url || defaultProjectImg}
                                            />
                                        </div>
                                    </div>

                                    <div className="self-stretch rounded overflow-hidden flex flex-col items-start justify-center pt-2 px-0 pb-0 gap-2 text-base text-gray font-manrope">
                                        <hr className="bdr-custom-taskcrad" />
                                        <div className="self-stretch flex flex-row items-center justify-between gap-0 text-xl">
                                            <b className="relative">{project?.name}</b>
                                            <div className="w-[130px] relative text-base text-dimgray inline-block shrink-0">
                                                <span>Members - </span>
                                                <span className="font-semibold">({project?.members?.length})</span>
                                            </div>
                                        </div>
                                        <hr className="bdr-custom-taskcrad" />
                                        <div className="self-stretch flex flex-col items-start justify-start gap-2 text-dimgray">
                                            <div className="self-stretch flex flex-row items-start justify-start gap-2">
                                                <div className="w-[130px] relative inline-block shrink-0">Project Owner:</div>
                                                <div className="relative font-semibold text-gray">{project?.created_by_name}</div>
                                            </div>
                                            <div className="self-stretch flex flex-row items-start justify-start gap-2">
                                                <div className="w-[130px] relative inline-block shrink-0">Description:</div>
                                                <div className="flex-1 relative font-semibold text-gray">
                                                    {project?.tagline || 'No description'}
                                                </div>
                                            </div>
                                        </div>
                                        <hr className="bdr-custom-taskcrad" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div >
            {projectsDataLoading && <Loader />
            }
        </>
    );
};

export default Projects;
